import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Login.css";

// Guest entry disabled; original Guest logic retained.
const GUEST_ACCESS_ENABLED = false;

const Login = ({ onLogin }) => {
  const [isRegistering, setIsRegistering] = useState(false);
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showResendVerification, setShowResendVerification] = useState(false);

  // Password reset
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetIdentifier, setResetIdentifier] = useState("");
  const [resetRequestPending, setResetRequestPending] = useState(false);

  const navigate = useNavigate();

  // Always same-origin.
  // Backend routes are mounted under /backend on SmarterASP.
  const API = "/backend";

  // Google Ads conversion: Scoldy Registration Completed.
  // Fires only after the backend confirms a new user was created.
  const fireSignupConversion = () => {
    console.log("✅ fireSignupConversion called");

    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      console.log("✅ gtag exists, sending signup conversion");

      window.gtag("event", "conversion", {
        send_to: "AW-16721690472/oFNhCMOB-7gcEOj-wqU-",
        value: 1.0,
        currency: "CAD",
      });
    } else {
      console.warn("� ️ gtag not available; signup conversion not sent");
    }
  };

  const handleLogin = async (event) => {
    event.preventDefault();

    try {
      const response = await fetch(`${API}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ username, password }),
      });

      if (!response.ok) {
        let errorMessage = "Login failed.";
        let emailVerificationRequired = false;

        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
          emailVerificationRequired =
            errorData.email_verification_required === true;
        } catch (e) {}

        setShowResendVerification(emailVerificationRequired);

        throw new Error(errorMessage);
      }

      const data = await response.json();

      setShowResendVerification(false);

      localStorage.setItem("currentUser", JSON.stringify(data));
      localStorage.removeItem("isGuest");
      onLogin(data);

      const groupRes = await fetch(`${API}/api/group/${data.group_id}`, {
        credentials: "include",
      });

      if (groupRes.ok) {
        const groupLimits = await groupRes.json();
        localStorage.setItem("groupLimits", JSON.stringify(groupLimits));
        console.log("✅ Group limits loaded:", groupLimits);
      } else {
        console.warn("� ️ Failed to load group limits.");
      }

      alert(`Welcome ${data.username}`);
      navigate("/");
    } catch (error) {
      console.error("Error:", error.message);
      alert("Login error: " + error.message);
    }
  };

  const handleRegister = async (event) => {
    event.preventDefault();

    if (password !== confirmPassword) {
      alert("Passwords do not match.");
      return;
    }

    if (!termsAccepted) {
      alert(
        "You must agree to the Terms of Service and Privacy Policy before registering."
      );
      return;
    }

    try {
      const response = await fetch(`${API}/users`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ username, email, password }),
      });

      let result = null;

      try {
        result = await response.json();
      } catch (e) {}

      if (!response.ok || !result || result.success !== true) {
        alert((result && result.error) || "Registration failed.");
        return;
      }

      console.log("✅ Registration accepted by backend. Result:", result);
      fireSignupConversion();

      alert(
        (result && result.message) ||
          "Registration received. Please check your email and verify your address before logging in."
      );

      setIsRegistering(false);
      setShowResendVerification(false);
      setShowForgotPassword(false);
      setUsername("");
      setEmail("");
      setPassword("");
      setConfirmPassword("");
      setTermsAccepted(false);
    } catch (error) {
      console.error("Error:", error.message);
      alert("Registration error: " + error.message);
    }
  };

  const handleResendVerification = async () => {
    if (!username || !password) {
      alert(
        "Enter your username and password before requesting another verification email."
      );
      return;
    }

    try {
      const response = await fetch(`${API}/resend-verification`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ username, password }),
      });

      let result = null;

      try {
        result = await response.json();
      } catch (e) {}

      if (!response.ok || !result || result.success !== true) {
        alert(
          (result && result.error) ||
            "The verification email could not be sent."
        );
        return;
      }

      alert(
        result.message ||
          "A new verification email has been sent. Please check your email and use the new verification link."
      );
    } catch (error) {
      console.error("Error resending verification email:", error.message);

      alert(
        "The verification email could not be sent right now. Please try again later."
      );
    }
  };

  const handleForgotPassword = async (event) => {
    event.preventDefault();

    const identifier = resetIdentifier.trim();

    if (!identifier) {
      alert("Enter your username or email address.");
      return;
    }

    setResetRequestPending(true);

    try {
      const response = await fetch(`${API}/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ identifier }),
      });

      let result = null;

      try {
        result = await response.json();
      } catch (e) {}

      if (!response.ok || !result || result.success !== true) {
        alert(
          (result && result.error) ||
            "The password reset request could not be processed right now."
        );
        return;
      }

      alert(
        result.message ||
          "If a matching verified Scoldy account exists, a password reset email has been sent."
      );

      setShowForgotPassword(false);
      setResetIdentifier("");
    } catch (error) {
      console.error("Error requesting password reset:", error.message);

      alert(
        "The password reset request could not be processed right now. Please try again later."
      );
    } finally {
      setResetRequestPending(false);
    }
  };

  // Guest login.
  // The audit request records the guest IP and user agent.
  // Guest access continues even if the audit request fails.
  const handleGuest = async () => {
    if (!GUEST_ACCESS_ENABLED) return;
    try {
      const auditResponse = await fetch(`${API}/guest-login`, {
        method: "POST",
        credentials: "include",
      });

      if (!auditResponse.ok) {
        console.warn("Guest access audit could not be recorded.");
      }
    } catch (error) {
      console.warn("Guest access audit request failed:", error.message);
    }

    const guestUser = {
      id: 0,
      username: "guest",
      email: null,
      group_id: 0,
      thumbnail: "/images/default-thumbnail.jpg",
      isGuest: true,
    };

    const guestLimits = {
      id: 0,
      name: "Guest",
      postLimit: 0,
      mediaLimit: 0,
      editTimeout: 0,
      priorityBoost: 0,
      isGuest: true,
    };

    localStorage.setItem("currentUser", JSON.stringify(guestUser));
    localStorage.setItem("groupLimits", JSON.stringify(guestLimits));
    localStorage.setItem("isGuest", "true");

    onLogin(guestUser);
    alert("Continuing as Guest (limited features).");
    navigate("/");
  };

  // Google Play listing
  const GOOGLE_PLAY_URL =
    "https://play.google.com/store/apps/details?id=com.highroad.truetalksocial";

  return (
    <div className="welcome-container">
      <div className="login-container">
        <h2>{isRegistering ? "Create an Account" : "Scoldy Social"}</h2>

        {!showForgotPassword ? (
          <>
            <form onSubmit={isRegistering ? handleRegister : handleLogin}>
              <div className="form-group">
                <input
                  type="text"
                  placeholder="Enter your username"
                  value={username}
                  onChange={(e) => {
                    setUsername(e.target.value);
                    setShowResendVerification(false);
                  }}
                  required
                  autoComplete="username"
                />
              </div>

              {isRegistering && (
                <div className="form-group">
                  <input
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                  />
                </div>
              )}

              <div className="form-group">
                <input
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setShowResendVerification(false);
                  }}
                  required
                  autoComplete={
                    isRegistering ? "new-password" : "current-password"
                  }
                />
              </div>

              {isRegistering && (
                <div className="form-group">
                  <input
                    type="password"
                    placeholder="Confirm your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                  />
                </div>
              )}

              {isRegistering && (
                <div className="registration-agreement">
                  <label>
                    <input
                      type="checkbox"
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                    />
                    <span>
                      I agree to the{" "}
                      <a
                        href="/terms-of-service.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Terms of Service
                      </a>{" "}
                      and{" "}
                      <a
                        href="/scoldyprivacy.html"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Privacy Policy
                      </a>
                      .
                    </span>
                  </label>
                </div>
              )}

              <button type="submit" className="submit-button">
                {isRegistering ? "Register" : "Login"}
              </button>
            </form>

            {!isRegistering && showResendVerification && (
              <div style={{ marginTop: 10, textAlign: "center" }}>
                <button
                  type="button"
                  className="guest-button"
                  onClick={handleResendVerification}
                >
                  Resend Verification Email
                </button>

                <div style={{ marginTop: 6, fontSize: 12, opacity: 0.8 }}>
                  Use this if you did not receive the verification email or your
                  verification link has expired.
                </div>
              </div>
            )}

            {GUEST_ACCESS_ENABLED && !isRegistering && (
              <div style={{ marginTop: 10, textAlign: "center" }}>
                <button
                  type="button"
                  className="guest-button"
                  onClick={handleGuest}
                >
                  Continue as Guest
                </button>

                <div style={{ marginTop: 6, fontSize: 12, opacity: 0.8 }}>
                  Guest mode has limited features (no posting, boosting, or
                  search).
                </div>
              </div>
            )}

            <div
              className="auth-links"
              style={{ marginTop: 12, textAlign: "center" }}
            >
              {!isRegistering && (
                <div style={{ marginBottom: 8 }}>
                  <button
                    type="button"
                    className="link-button"
                    style={{
                      fontSize: 14,
                      textDecoration: "underline",
                    }}
                    onClick={() => {
                      setResetIdentifier(username);
                      setShowForgotPassword(true);
                      setShowResendVerification(false);
                    }}
                  >
                    Forgot your password?
                  </button>
                </div>
              )}

              <div>
                {isRegistering ? (
                  <p>
                    Already have an account?{" "}
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => {
                        setIsRegistering(false);
                        setTermsAccepted(false);
                        setShowResendVerification(false);
                      }}
                    >
                      Log In
                    </button>
                  </p>
                ) : (
                  <p>
                    Don't have an account?{" "}
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => {
                        setIsRegistering(true);
                        setTermsAccepted(false);
                        setShowResendVerification(false);
                      }}
                    >
                      Sign Up
                    </button>
                  </p>
                )}
              </div>
            </div>
          </>
        ) : (
          <div>
            <h3 style={{ textAlign: "center" }}>Reset Your Password</h3>

            <p
              style={{
                textAlign: "center",
                fontSize: 14,
                lineHeight: 1.5,
              }}
            >
              Enter your Scoldy username or verified email address. If a
              matching verified account exists, we will send a password reset
              link to the registered email address.
            </p>

            <form onSubmit={handleForgotPassword}>
              <div className="form-group">
                <input
                  type="text"
                  placeholder="Username or email address"
                  value={resetIdentifier}
                  onChange={(e) => setResetIdentifier(e.target.value)}
                  required
                  autoComplete="username"
                />
              </div>

              <button
                type="submit"
                className="submit-button"
                disabled={resetRequestPending}
              >
                {resetRequestPending
                  ? "Sending..."
                  : "Send Password Reset Email"}
              </button>
            </form>

            <div style={{ marginTop: 12, textAlign: "center" }}>
              <button
                type="button"
                className="link-button"
                onClick={() => {
                  setShowForgotPassword(false);
                  setResetIdentifier("");
                }}
              >
                Back to Login
              </button>
            </div>
          </div>
        )}

        <div className="store-badges">
          <p className="store-badges-title">Get the mobile app</p>

          <div className="store-badges-row">
            <a
              href="https://apps.apple.com/app/id6756085597"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src="/images/appstore-badge.png"
                alt="Download on the App Store"
                className="store-badge"
              />
            </a>

            <a
              href={GOOGLE_PLAY_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{ marginLeft: 12 }}
            >
              <img
                src="/images/googleplaybadge.png"
                alt="Get it on Google Play"
                className="store-badge"
              />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;